import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { QuickCreateItemGroup } from '../components/QuickCreateModals';
import ExportButton from '../components/ExportButton';
import PDFExportButton from '../components/PDFExportButton';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [showGroupModal, setShowGroupModal] = useState(false);

  const emptyForm = {
    sku: '', name: '', product_category: 'fg', item_group: '', size: '', micron: '',
    opening_stock_rate: 0, location: '', vat_rate: 0,
    quantity: 0, unit: '', unit_price: 0, min_stock_level: 0,
    final_product_type: '', base_unit: '', conversion_factor: 1,
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, grpRes, locRes, unitRes] = await Promise.all([
        api.get('/sales/inventory'), api.get('/sales/item-groups'), api.get('/sales/locations'), api.get('/sales/units')
      ]);
      setItems(invRes.data); setItemGroups(grpRes.data); setLocations(locRes.data); setUnits(unitRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      sku: item.sku, name: item.name, product_category: item.product_category || 'fg',
      item_group: item.item_group || '', size: item.size || '', micron: item.micron ?? '',
      opening_stock_rate: item.opening_stock_rate || 0, location: item.location || '',
      vat_rate: item.vat_rate || 0,
      quantity: item.quantity, unit: item.unit, unit_price: item.unit_price || 0,
      min_stock_level: item.min_stock_level,
      final_product_type: item.final_product_type || '', base_unit: item.base_unit || '',
      conversion_factor: item.conversion_factor || 1,
    });
    setShowModal(true);
  };

  const openCreateModal = () => { setEditingItem(null); setFormData({ ...emptyForm }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, micron: (formData.micron === '' || formData.micron === null) ? null : parseFloat(formData.micron) };
    try {
      if (editingItem) {
        const { sku, ...updateData } = payload;
        await api.put(`/sales/inventory/${editingItem.id}`, updateData);
      } else {
        await api.post('/sales/inventory', payload);
      }
      setShowModal(false); fetchData();
    } catch (error) { alert(error.response?.data?.detail || 'Failed to save item'); }
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/sales/inventory/${id}`); fetchData(); }
    catch (error) { alert(error.response?.data?.detail || 'Failed'); }
  };

  const catLabels = { rm: 'Raw Material', wip: 'WIP', fg: 'Finished Good' };

  const filteredItems = items.filter((item) => {
    const matchesSearch = item.sku.toLowerCase().includes(searchTerm.toLowerCase()) || item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const isLow = item.quantity <= item.min_stock_level;
    const matchesStock = stockFilter === 'all' || (stockFilter === 'low' && isLow) || (stockFilter === 'ok' && !isLow);
    const matchesCat = categoryFilter === 'all' || item.product_category === categoryFilter;
    const matchesGroup = groupFilter === 'all' || item.item_group === groupFilter;
    return matchesSearch && matchesStock && matchesCat && matchesGroup;
  });

  return (
    <div data-testid="inventory-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Inventory Management</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/export/inventory" filename="inventory_export.xlsx" />
          <PDFExportButton endpoint="/export/pdf/inventory" filename="inventory_export.pdf" />
          <button onClick={openCreateModal} className="btn btn-primary" data-testid="add-inventory-btn"><Plus size={16} /> Add Item</button>
        </div>
      </div>

      <div className="card mb-4">
        <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 2, minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search by SKU or name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" style={{ width: '150px' }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="category-filter">
            <option value="all">All Categories</option>
            <option value="rm">Raw Material</option>
            <option value="wip">WIP</option>
            <option value="fg">Finished Good</option>
          </select>
          <select className="form-select" style={{ width: '150px' }} value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="group-filter">
            <option value="all">All Groups</option>
            {itemGroups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
          <select className="form-select" style={{ width: '140px' }} value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} data-testid="stock-filter">
            <option value="all">All Stock</option>
            <option value="low">Low Stock</option>
            <option value="ok">In Stock</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading inventory...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>SKU</th><th>Name</th><th>Category</th><th>Group</th><th>Size</th>
                  <th>Location</th><th className="text-right">Rate</th>
                  <th className="text-right">Qty</th><th>Unit</th>
                  <th className="text-right">Unit Price</th><th className="text-right">Min Level</th>
                  <th>VAT%</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan={14} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No items found</td></tr>
                ) : filteredItems.map((item) => (
                  <tr key={item.id} data-testid={`inventory-row-${item.sku}`}>
                    <td className="font-mono">{item.sku}</td>
                    <td>{item.name}</td>
                    <td><span className={`badge ${item.product_category === 'fg' ? 'badge-success' : item.product_category === 'rm' ? 'badge-info' : 'badge-warning'}`}>{catLabels[item.product_category] || item.product_category}</span></td>
                    <td>{item.item_group || '-'}</td>
                    <td>{item.size || '-'}</td>
                    <td>{item.location || '-'}</td>
                    <td className="text-right tabular-nums">{item.opening_stock_rate ? item.opening_stock_rate.toLocaleString() : '-'}</td>
                    <td className="text-right tabular-nums">{item.quantity}</td>
                    <td>{item.unit}</td>
                    <td className="text-right tabular-nums">{item.unit_price ? item.unit_price.toLocaleString() : '-'}</td>
                    <td className="text-right tabular-nums">{item.min_stock_level}</td>
                    <td className="text-sm">{item.vat_rate || 0}%</td>
                    <td>
                      {item.quantity <= item.min_stock_level ? (
                        <span className="badge badge-warning" data-testid={`status-${item.sku}`}>Low Stock</span>
                      ) : (
                        <span className="badge badge-success" data-testid={`status-${item.sku}`}>In Stock</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEditModal(item)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${item.sku}`}><Edit size={14} /></button>
                        <button onClick={() => deleteItem(item.id, item.name)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${item.sku}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="inventory-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-3">
                  <div className="form-group"><label className="form-label">SKU *</label><input type="text" className="form-input" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} required disabled={!!editingItem} data-testid="sku-input" /></div>
                  <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="name-input" /></div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-select" value={formData.product_category} onChange={(e) => setFormData({ ...formData, product_category: e.target.value })} data-testid="category-select">
                      <option value="rm">Raw Material</option><option value="wip">WIP</option><option value="fg">Finished Good</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">Item Group <button type="button" onClick={() => setShowGroupModal(true)} className="btn btn-ghost btn-sm" style={{ padding: '0 4px', marginLeft: '4px' }}><Plus size={12} /></button></label>
                    <select className="form-select" value={formData.item_group} onChange={(e) => setFormData({ ...formData, item_group: e.target.value })} data-testid="group-select">
                      <option value="">None</option>
                      {itemGroups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Size</label><input type="text" className="form-input" value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} placeholder="e.g. 10mm, XL" data-testid="size-input" /></div>
                  <div className="form-group"><label className="form-label">Micron</label><input type="number" step="0.01" className="form-input" value={formData.micron} onChange={(e) => setFormData({ ...formData, micron: e.target.value })} placeholder="e.g. 20" data-testid="micron-input" /></div>
                  <div className="form-group"><label className="form-label">Location</label>
                    <input type="text" className="form-input" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Warehouse / Rack" data-testid="location-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3">
                  <div className="form-group"><label className="form-label">VAT %</label><input type="number" step="0.01" className="form-input" value={formData.vat_rate} onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 0 })} data-testid="gst-rate-input" /></div>
                  <div className="form-group"><label className="form-label">Opening Stock Rate</label><input type="number" step="0.01" className="form-input" value={formData.opening_stock_rate} onChange={(e) => setFormData({ ...formData, opening_stock_rate: parseFloat(e.target.value) || 0 })} data-testid="opening-rate-input" /></div>
                </div>
                <div className="grid grid-cols-4">
                  <div className="form-group"><label className="form-label">Unit Price</label><input type="number" step="0.01" className="form-input" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} data-testid="price-input" /></div>
                  <div className="form-group"><label className="form-label">Unit *</label><select className="form-select" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} required data-testid="unit-input"><option value="">Select Unit...</option>{units.map(u => <option key={u.id} value={u.symbol}>{u.name} ({u.symbol})</option>)}</select></div>
                  <div className="form-group"><label className="form-label">Quantity *</label><input type="number" className="form-input" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} required data-testid="quantity-input" /></div>
                  <div className="form-group"><label className="form-label">Min Stock</label><input type="number" className="form-input" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })} data-testid="min-stock-input" /></div>
                </div>
                <div className="grid grid-cols-3">
                  <div className="form-group">
                    <label className="form-label">Final Product Type {formData.product_category !== 'fg' && formData.product_category !== 'wip' ? <span className="text-xs text-muted">(FG/WIP only)</span> : ''}</label>
                    <select className="form-select" value={formData.final_product_type} onChange={(e) => setFormData({ ...formData, final_product_type: e.target.value })} data-testid="final-product-type-select">
                      <option value="">None</option>
                      <option value="roll">Roll</option>
                      <option value="pouch">Pouch</option>
                      <option value="sleeve">Sleeve</option>
                      <option value="hair_bag">Hair Bag</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Base Unit</label>
                    <select className="form-select" value={formData.base_unit} onChange={(e) => setFormData({ ...formData, base_unit: e.target.value })} data-testid="base-unit-select">
                      <option value="">Same as Unit</option>
                      {units.map(u => <option key={u.id} value={u.symbol}>{u.name} ({u.symbol})</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Conversion Factor <span className="text-xs text-muted">(1 unit = ? base)</span></label><input type="number" step="0.0001" className="form-input" value={formData.conversion_factor} onChange={(e) => setFormData({ ...formData, conversion_factor: parseFloat(e.target.value) || 1 })} data-testid="conversion-factor-input" /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" data-testid="cancel-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-inventory-btn">{editingItem ? 'Update Item' : 'Add Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGroupModal && (
        <QuickCreateItemGroup
          onCreated={(group) => { setItemGroups([...itemGroups, group]); setFormData({ ...formData, item_group: group.name }); }}
          onClose={() => setShowGroupModal(false)}
        />
      )}
    </div>
  );
};

export default Inventory;
